import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import isCableURL from "../../utils/isCableURL";

import axios from "connection";

import ConfigSlice, { ConfigSliceFromRoot } from "types/ConfigSlice";

import MonetaryFormat from "types/MonetaryFormat";
import { getDeviceSize, getDeviceType } from "utils/deviceDetect";
import getOpco from "utils/getOpco";
import { getSource } from "utils/getSource";
import LoginUrl from "types/LoginUrl";
import { clearOffersState } from "state/offers-slice";
import { clearCartState } from "state/cart-slice";

import { API_URL, PPE_PATH } from "config";
import Accessibility from "types/Accessibility";
import { isFeatureActive } from "utils/getFeature";
import { MedalliaData } from "types/MedalliaData";
import { AppDispatch, RootState } from "state/reducers-combiner";

type UlfEventPayload = {
  jsessionId?: string;
  uid?: string;
  isTrackingAllowed?: string | boolean;
  [key: string]: unknown;
};

const initialState: ConfigSlice = {
  loaded: false,
  requiresLandingPage: false,
  openidUrls: [],
  modal: {
    showModal: false,
    errorModal: false,
    cookieModal: false,
    title: "",
    description: "",
    cross: false,
    children: null,
    textAlign: null,
    icon: null,
    type: "",
    size: "small",
    customButtons: null,
  },
  loader: {
    showLoader: true,
    messageHeader: "",
    messageText: "",
  },
  IframeLayer: {
    showIframeLayer: false,
    url: null,
  },
  toast: {
    show: false,
    icon: null,
    children: null,
    type: "",
    id: "",
    ids: [],
  },
  isStickyAreaVisible: false,
  isMenuOpen: false,
  cdnUrl: "",
  survey: {
    config: {
      enabledForMarket: false,
      sdkUrl: "",
      forms: [],
    },
    history: [],
  },
  surveyLoading: false,
};

const DEEPLINK_SKIP_LANDING_PAGE_FLAG = "Skip Landing Page for Deeplink";

const isMarketplaceDeeplinkUrl = (): boolean => {
  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const hasSubscriptionPlusOpcoPath = pathSegments[0] === "subscriptionplus" && Boolean(pathSegments[1]);
  const searchParams = new URLSearchParams(window.location.search);
  const deeplinkParamNames = ["deeplink", "s", "partner"];
  const hasDeeplinkParam = [...searchParams.entries()].some(
    ([name, value]) => deeplinkParamNames.includes(name.toLowerCase()) && Boolean(value),
  );

  return hasSubscriptionPlusOpcoPath && hasDeeplinkParam;
};

const shouldSkipLandingPageForDeeplink = (): boolean =>
  isFeatureActive(DEEPLINK_SKIP_LANDING_PAGE_FLAG) && isMarketplaceDeeplinkUrl();

export const fetchConfigFromAPI = createAsyncThunk("config/fetchConfig", async () => {
  const source = getSource();
  const queryParams = source ? `&s=${source}` : "";

  try {
    const result = await axios.get(`${API_URL}${PPE_PATH}/config?opco=${getOpco()}${queryParams}`);

    return result.data;
  } catch (error) {
    return Promise.reject(error);
  }
});

export const fetchSurveyData = createAsyncThunk<MedalliaData>(
  "medallia/fetchSurveyData",
  async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/survey`);

      return (await response.data) as MedalliaData;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  {
    // Skip if a request is already in flight (e.g. contract switch and the auto consent handler racing each other)
    condition: (_arg, { getState }) => !(getState() as ConfigSliceFromRoot).config.surveyLoading,
  },
);

export const sendULFEvent = createAsyncThunk("config/sendULFEvent", async (data: UlfEventPayload) => {
  const body = {
    channel: getDeviceSize(),
    design: "visual",
    "offer-name": null,
    partner: null,
    page: window.location.pathname,
    source: "subscriptionplus",
    "x-vf-trace-timestamp": Date.now(),
    reason: null,
    "device-type": getDeviceType(),
    proposition: "Subscription_Plus",
  };

  const initialData = {
    "x-vf-trace-timestamp": Date.now(),
    "jsession-id": data.jsessionId,
    "usecase-id": data.uid,
    "cookie-accepted": data.isTrackingAllowed === true || data.isTrackingAllowed === "true", // Normalize to boolean
  };

  // Always merge body + data, then add initialData (fixes pre-login logging)
  const postData = Object.assign({}, body, data, initialData);

  try {
    const result = await axios.post(`${API_URL}${PPE_PATH}/ulf`, postData);

    return result;
  } catch (error) {
    return Promise.reject(error);
  }
});

export const redirectToID = createAsyncThunk<void, unknown, { state: RootState; dispatch: AppDispatch }>(
  "redirect/vodafoneID",
  async (_arg, { getState, dispatch }) => {
    const isLanadingPageEnabled = isFeatureActive("Enable New Landing Page");

    if (isLanadingPageEnabled && !shouldSkipLandingPageForDeeplink()) {
      dispatch(clearCartState());
      dispatch(clearOffersState());
      dispatch(setRequiredLandingPage(true));
      return;
    }

    const { config } = getState();
    const url = config?.openidUrls?.find((el: LoginUrl) =>
      isCableURL() ? el.type === "silver" : el.type === "gold" || el.type === "bronze",
    )?.url;

    if (url) {
      window.location.assign(url);
      dispatch(clearCartState());
      dispatch(clearOffersState());
      dispatch(clearConfigState());
    }
  },
);

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setConfig: (state, action) => {
      return {
        ...state,
        loaded: true,
        openidUrls: { ...action.payload.openidUrls },
      };
    },
    setRequiredLandingPage: (state, action) => {
      return {
        ...state,
        requiresLandingPage: action.payload,
      };
    },
    setModal: (state, action) => {
      return {
        ...state,
        modal: {
          ...initialState.modal,
          ...action.payload,
        },
      };
    },
    clearConfigState: () => {
      return {
        ...initialState,
      };
    },
    setLoader: (state, action) => {
      return {
        ...state,
        loader: {
          ...state.loader,
          ...action.payload,
        },
      };
    },
    setIframeLayer: (state, action) => {
      return {
        ...state,
        IframeLayer: {
          ...state.IframeLayer,
          ...action.payload,
        },
      };
    },
    setToast: (state, action) => {
      return {
        ...state,
        toast: {
          ...state.toast,
          ...action.payload,
        },
      };
    },
    setIsStickyAreaVisible: (state, action) => {
      return {
        ...state,
        isStickyAreaVisible: action.payload,
      };
    },
    setIsMenuOpen: (state, action) => {
      return {
        ...state,
        isMenuOpen: action.payload,
      };
    },
    toggleSurveyOff: (state) => {
      if (!state.survey) return;

      state.survey.config.enabledForMarket = initialState.survey?.config.enabledForMarket;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfigFromAPI.fulfilled, (state, action) => {
        state.openidUrls = [...action.payload.openidUrls];
        state.monetaryFormat = { ...action.payload.monetaryFormat };
        state.accessibility = { ...action.payload.accessibility };
        state.cdnUrl = action.payload.assets || "";
        state.loaded = true;
      })
      .addCase(fetchConfigFromAPI.pending, (state) => {
        state.loaded = false;
      })
      .addCase(fetchConfigFromAPI.rejected, (state) => {
        state.loader.showLoader = false;
        state.modal.showModal = true;
        state.modal.errorModal = true;
      })
      .addCase(fetchSurveyData.fulfilled, (state, action) => {
        state.survey = state.survey || {
          config: {
            enabledForMarket: false,
            sdkUrl: "",
            forms: [],
          },
          history: [],
        };

        state.survey.config = action.payload.config;
        state.survey.history = action.payload.history;
        state.surveyLoading = false;
      })
      .addCase(fetchSurveyData.pending, (state) => {
        state.surveyLoading = true;
      })
      .addCase(fetchSurveyData.rejected, (state) => {
        state.surveyLoading = false;
      });
  },
});

export const {
  setConfig,
  setModal,
  clearConfigState,
  setLoader,
  setIframeLayer,
  setToast,
  setIsStickyAreaVisible,
  setIsMenuOpen,
  toggleSurveyOff,
  setRequiredLandingPage,
} = configSlice.actions;

export const configState = (state: ConfigSliceFromRoot): ConfigSlice => state.config;

export const monetaryConfig = (state: ConfigSliceFromRoot): MonetaryFormat | undefined => state.config.monetaryFormat;

export const accessibilityConfig = (state: ConfigSliceFromRoot): Accessibility | undefined =>
  state.config.accessibility;

export default configSlice.reducer;
